import React from "react";
import {useTranslation} from "react-i18next";
import {useSales} from "./SalesContext";
import useCoreSiteEngagements from "@/hooks/useCoreSiteEngagements";
import Accordion from "@/components/Accordion";
import AccordionItem from "@/components/AccordionItem";
import {CalendarIcon, ClipboardDocumentListIcon, FolderIcon, NewspaperIcon} from "@heroicons/react/24/outline";
import Loader from "@/components/Loader";
import ProjectListItem from "@/pages/workspaces/sales/ProjectItemList";
import EventListItem from "@/pages/workspaces/sales/EventListItem";
import OfferListItem from "@/pages/workspaces/sales/OfferListItem";
import EvaluationListItem from "@/pages/workspaces/sales/EvaluationListItem";

function getSectionIcon(section) {
  const key = section?.toLowerCase();
  switch (key) {
    case 'projects':
      return FolderIcon;
    case 'events':
      return CalendarIcon;
    case 'offers':
      return NewspaperIcon;
    case 'evaluations':
      return ClipboardDocumentListIcon;
    default:
      return null;
  }
}

function SectionListItem({sectionKey, item}) {
  if (sectionKey === 'projects') return <ProjectListItem project={item}/>;
  if (sectionKey === 'events') return <EventListItem event={item}/>;
  if (sectionKey === 'offers') return <OfferListItem offer={item}/>;
  if (sectionKey === 'evaluations') return <EvaluationListItem evaluation={item}/>;
  return null
}

export default function SiteEngagements() {
  const {t} = useTranslation();
  const {state} = useSales();
  const {clickedItem} = state;
  const siteId = clickedItem?.properties?.id;

  const {
    data: engagements,
    isLoading: isLoadingEngagements,
    isError: isEngagementsError,
    error: engagementsError
  } = useCoreSiteEngagements(siteId);

  if (!clickedItem || clickedItem.cluster) return (
    <p className="text-sm test-base-content">
      {t('workspace.sales.siteMap.no_engagements')}
    </p>
  );

  return (
    <div className="space-y-1">
      <p className="font-semibold text-gray-900">
        {t('workspace.sales.siteMap.recent_engagements')}
      </p>
      {/*{clickedItem && <pre>{JSON.stringify(clickedItem, null, 2)}</pre>}*/}

      {isLoadingEngagements && (
        <p className="text-sm test-base-content animate-pulse">
          <Loader/>
        </p>
      )}

      {isEngagementsError && (
        <p className="text-sm text-error">
          {t('error')}: {engagementsError?.message}
        </p>
      )}

      {engagements && (
        <div className="space-y-4">
          <Accordion>
            {['projects', 'events', 'offers', 'evaluations'].map((sectionKey) => {
              const section = engagements[sectionKey];
              const icon = getSectionIcon(sectionKey);

              return (
                <AccordionItem
                  key={`aci-${siteId}-${sectionKey}`}
                  id={`aci-${siteId}-${sectionKey}`}
                  title={t(sectionKey, {count: 2})}
                  badge={section.total}
                  icon={icon}
                >
                  <ul className="text-xs space-y-1 mt-1">
                    {section.data.map((item) => (
                      <SectionListItem key={item.id} item={item} sectionKey={sectionKey}/>
                    ))}
                    {section.total === 0 && (
                      <div className="text-base-content italic">
                        {t(`workspace.sales.no_${sectionKey}`)}
                      </div>
                    )}
                    {section.total > section.data.length && (
                      <div className="text-base-content italic">
                        + {section.total - section.data.length} {t('workspace.sales.more_ellipsis')}
                      </div>
                    )}
                  </ul>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}


    </div>
  );
}
